import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Card, CardContent, Button, Chip, TextField, Select, MenuItem,
  FormControl, InputLabel, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Dialog, DialogTitle, DialogContent, DialogActions, Grid, CircularProgress,
  Alert, IconButton, Tooltip, Avatar, Divider, Stack, Tabs, Tab, Collapse,
  useMediaQuery, Snackbar, LinearProgress, alpha,
} from '@mui/material';
import {
  Support as SupportIcon, AccessTime as ClockIcon, People as PeopleIcon,
  Search as SearchIcon, Visibility as ViewIcon, Telegram as TelegramIcon,
  WhatsApp as WhatsAppIcon, Delete as DeleteIcon, SwapHoriz as StatusIcon,
  Payment as PaymentIcon, Help as HelpIcon, Warning as WarningIcon,
  CalendarMonth as CalendarIcon, EventRepeat as RescheduleIcon,
  Cancel as CancelIcon, ReportProblem as ComplaintIcon, AttachMoney as MoneyIcon,
  LocalHospital as MedicalIcon, AllInclusive as AllIcon, Chat as ChatIcon,
  ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon, Comment as CommentIcon,
  Close as CloseIcon, CheckCircle as ResolvedIcon, Build as BuildIcon,
  Refresh as RefreshIcon, TrendingUp as TrendingIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { getApiUrl } from '../../config/environment';

const API_BASE_URL = getApiUrl();

type SupportType =
  | 'technical_support' | 'billing_support' | 'general_support'
  | 'urgencia' | 'agendamento' | 'remarcacao' | 'cancelamento'
  | 'reclamacao' | 'financeiro' | 'assistencia_medica';

interface SupportLead {
  id: string;
  contact_id: string;
  lead_name: string;
  lead_email?: string | null;
  lead_phone?: string | null;
  support_type: SupportType;
  status: 'pending' | 'in_progress' | 'resolved' | 'closed';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  original_stage?: number | null;
  original_pipeline?: string | null;
  notes?: string | null;
  comment?: string | null;
  custom_fields: Record<string, any>;
  metadata: Record<string, any>;
  created_at: string;
  updated_at: string;
}

interface SupportStats {
  total: number;
  pending: number;
  in_progress: number;
  resolved: number;
  closed: number;
  by_type: Record<string, number>;
  technical_support?: number;
  billing_support?: number;
  general_support?: number;
  priority: { low: number; normal: number; high: number; urgent: number };
}

interface TabDef {
  key: string;
  label: string;
  icon: React.ReactElement;
  color: string;
  supportType: string;
}

const TAB_DEFINITIONS: TabDef[] = [
  { key: 'all', label: 'Todos', icon: <AllIcon />, color: '#0f766e', supportType: '' },
  { key: 'urgencia', label: 'Urgência', icon: <WarningIcon />, color: '#dc2626', supportType: 'urgencia' },
  { key: 'agendamento', label: 'Agendamento', icon: <CalendarIcon />, color: '#059669', supportType: 'agendamento' },
  { key: 'remarcacao', label: 'Remarcação', icon: <RescheduleIcon />, color: '#d97706', supportType: 'remarcacao' },
  { key: 'cancelamento', label: 'Cancelamento', icon: <CancelIcon />, color: '#7c3aed', supportType: 'cancelamento' },
  { key: 'reclamacao', label: 'Reclamação', icon: <ComplaintIcon />, color: '#e11d48', supportType: 'reclamacao' },
  { key: 'financeiro', label: 'Financeiro', icon: <MoneyIcon />, color: '#0891b2', supportType: 'financeiro' },
  { key: 'assistencia_medica', label: 'Assistência Médica', icon: <MedicalIcon />, color: '#4f46e5', supportType: 'assistencia_medica' },
  { key: 'technical_support', label: 'Suporte Técnico', icon: <BuildIcon />, color: '#2563eb', supportType: 'technical_support' },
  { key: 'billing_support', label: 'Suporte Billing', icon: <PaymentIcon />, color: '#ea580c', supportType: 'billing_support' },
  { key: 'general_support', label: 'Suporte Geral', icon: <HelpIcon />, color: '#64748b', supportType: 'general_support' },
];

const SUPPORT_TYPE_MAP: Record<string, { label: string; icon: React.ReactElement; color: string }> = {};
TAB_DEFINITIONS.forEach(t => { if (t.supportType) SUPPORT_TYPE_MAP[t.supportType] = { label: t.label, icon: t.icon, color: t.color }; });

const STATUS_MAP: Record<string, { label: string; color: string; bg: string }> = {
  pending: { label: 'Pendente', color: '#d97706', bg: '#fef3c7' },
  in_progress: { label: 'Em Andamento', color: '#2563eb', bg: '#dbeafe' },
  resolved: { label: 'Resolvido', color: '#059669', bg: '#d1fae5' },
  closed: { label: 'Encerrado', color: '#64748b', bg: '#f1f5f9' },
};

const SupportDashboard: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width:900px)');

  const [leads, setLeads] = useState<SupportLead[]>([]);
  const [stats, setStats] = useState<SupportStats | null>(null);
  const [loading, setLoading] = useState(true);

  const initialTab = (() => {
    const tabParam = searchParams.get('tab');
    if (tabParam) { const idx = TAB_DEFINITIONS.findIndex(t => t.key === tabParam); return idx >= 0 ? idx : 0; }
    return 0;
  })();
  const [activeTab, setActiveTab] = useState(initialTab);
  const [filters, setFilters] = useState({ status: '', search: '' });

  const [selectedLead, setSelectedLead] = useState<SupportLead | null>(null);
  const [resolveNotes, setResolveNotes] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [openStatusDialog, setOpenStatusDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [leadConversations, setLeadConversations] = useState<Map<string, { hasWhatsApp: boolean; hasTelegram: boolean; whatsappPath: string }>>(new Map());
  /** Enquanto o backend resolve inbox/conversa por lead — evita mostrar botão de chat “morto”. */
  const [resolvingChatLinks, setResolvingChatLinks] = useState(false);
  const chatLinksRequestSeq = useRef(0);
  const [expandedLeadId, setExpandedLeadId] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const currentTabDef = TAB_DEFINITIONS[activeTab];

  useEffect(() => { loadData(); }, [filters, activeTab]);

  const resolveChatLinks = async (leadList: SupportLead[]) => {
    if (leadList.length === 0) {
      setLeadConversations(new Map());
      setResolvingChatLinks(false);
      return;
    }
    const seq = ++chatLinksRequestSeq.current;
    setResolvingChatLinks(true);
    try {
      const ids = leadList.map((l) => l.id);
      const linksRes = await axios.post(`${API_BASE_URL}/support/leads/chat-links`, { ids });
      const links: Record<string, { inbox_path: string; conversation_id: string | null; has_chat: boolean }> =
        linksRes.data?.success ? linksRes.data.data || {} : {};

      const map = new Map<string, { hasWhatsApp: boolean; hasTelegram: boolean; whatsappPath: string }>();
      for (const lead of leadList) {
        const link = links[lead.id];
        const path = link?.inbox_path || '';
        const isTelegram = path.startsWith('/inbox/telegram');
        const hasChat = !!link?.has_chat;
        const existing = map.get(lead.contact_id);

        map.set(lead.contact_id, {
          hasWhatsApp: existing?.hasWhatsApp || (!isTelegram && hasChat),
          hasTelegram: existing?.hasTelegram || (isTelegram && hasChat),
          whatsappPath:
            existing?.whatsappPath && existing.whatsappPath !== '/inbox/whatsapp'
              ? existing.whatsappPath
              : !isTelegram && path
                ? path
                : existing?.whatsappPath || '/inbox/whatsapp',
        });
      }
      if (seq === chatLinksRequestSeq.current) {
        setLeadConversations(map);
      }
    } catch (e) {
      console.warn('resolveChatLinks falhou:', e);
      if (seq === chatLinksRequestSeq.current) {
        setLeadConversations(new Map());
      }
    } finally {
      if (seq === chatLinksRequestSeq.current) {
        setResolvingChatLinks(false);
      }
    }
  };

  const loadData = async () => {
    let leadsData: SupportLead[] = [];
    try {
      setLoading(true);
      const params: any = { ...filters, limit: 100 };
      if (currentTabDef.supportType) params.support_type = currentTabDef.supportType;
      const [leadsRes, statsRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/support/leads`, { params }),
        axios.get(`${API_BASE_URL}/support/stats`),
      ]);
      leadsData = leadsRes.data.data || [];
      setLeads(leadsData);
      setStats(statsRes.data.data || null);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
    void resolveChatLinks(leadsData);
  };

  const handleResolveLead = async (leadId: string) => {
    try {
      await axios.post(`${API_BASE_URL}/support/leads/${leadId}/resolve`, { notes: resolveNotes });
      setResolveNotes(''); setSelectedLead(null); setOpenDialog(false); loadData();
      setSnackbar({ open: true, message: 'Solicitação resolvida com sucesso', severity: 'success' });
    } catch (error) {
      console.error('Erro ao resolver:', error);
      setSnackbar({ open: true, message: 'Erro ao resolver solicitação', severity: 'error' });
    }
  };

  const handleUpdateStatus = async (leadId: string) => {
    try {
      await axios.put(`${API_BASE_URL}/support/leads/${leadId}`, { status: newStatus });
      setNewStatus(''); setSelectedLead(null); setOpenStatusDialog(false); loadData();
      setSnackbar({ open: true, message: 'Status atualizado', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: 'Erro ao atualizar status', severity: 'error' });
    }
  };

  const handleDeleteLead = async (leadId: string) => {
    try {
      await axios.delete(`${API_BASE_URL}/support/leads/${leadId}`);
      setSelectedLead(null); setOpenDeleteDialog(false); loadData();
      setSnackbar({ open: true, message: 'Solicitação excluída', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: 'Erro ao excluir', severity: 'error' });
    }
  };

  const getSupportTypeInfo = (type: string) => SUPPORT_TYPE_MAP[type] || { label: type, icon: <SupportIcon />, color: '#94a3b8' };

  const getTypeCount = (type: string): number => {
    if (!stats) return 0;
    if (stats.by_type?.[type] !== undefined) return stats.by_type[type];
    if (type === 'technical_support' && stats.technical_support !== undefined) return stats.technical_support;
    if (type === 'billing_support' && stats.billing_support !== undefined) return stats.billing_support;
    if (type === 'general_support' && stats.general_support !== undefined) return stats.general_support;
    return 0;
  };

  const formatDate = (d: string) => new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  const resolvedPct = stats && stats.total > 0 ? Math.round((stats.resolved / stats.total) * 100) : 0;

  if (loading && leads.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh', flexDirection: 'column', gap: 2 }}>
        <CircularProgress sx={{ color: '#0f766e' }} />
        <Typography sx={{ color: '#64748b', fontSize: '0.9rem' }}>Carregando solicitações...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, backgroundColor: 'transparent', minHeight: '100vh', width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, flexDirection: { xs: 'column', md: 'row' }, gap: 2, mb: 3 }}>
        <Box>
          <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 800, background: 'linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', letterSpacing: '-0.02em' }}>
            Central de Atendimento
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
            Gerencie solicitações de pacientes e equipe por categoria
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {stats && stats.pending > 0 && (
            <Chip label={`${stats.pending} pendente${stats.pending > 1 ? 's' : ''}`}
              sx={{ fontWeight: 600, bgcolor: '#fef3c7', color: '#92400e', border: '1px solid #fde68a' }} />
          )}
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadData}
            sx={{ borderColor: '#e2e8f0', color: '#64748b', textTransform: 'none', fontWeight: 600, '&:hover': { borderColor: '#14b8a6', color: '#0f766e' } }}>
            Atualizar
          </Button>
        </Box>
      </Box>

      {/* KPI Cards */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: 'Total', value: stats.total, icon: <PeopleIcon />, color: '#0f766e', bg: '#f0fdfa' },
            { label: 'Pendentes', value: stats.pending, icon: <ClockIcon />, color: '#d97706', bg: '#fffbeb' },
            { label: 'Em Andamento', value: stats.in_progress, icon: <TrendingIcon />, color: '#2563eb', bg: '#eff6ff' },
            { label: 'Resolvidos', value: stats.resolved, icon: <ResolvedIcon />, color: '#059669', bg: '#ecfdf5' },
          ].map((kpi) => (
            <Grid item xs={6} sm={3} key={kpi.label}>
              <Card sx={{ borderRadius: 3, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9', transition: 'all 0.2s', '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.08)', transform: 'translateY(-1px)' } }}>
                <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography sx={{ color: '#94a3b8', fontWeight: 500, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{kpi.label}</Typography>
                      <Typography variant="h4" sx={{ fontWeight: 800, color: kpi.color, mt: 0.5 }}>{kpi.value}</Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: kpi.bg, width: 44, height: 44, color: kpi.color }}>
                      {React.cloneElement(kpi.icon, { sx: { fontSize: 22 } })}
                    </Avatar>
                  </Box>
                  {kpi.label === 'Resolvidos' && stats.total > 0 && (
                    <Box sx={{ mt: 1.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8' }}>Taxa de resolução</Typography>
                        <Typography sx={{ fontSize: '0.7rem', color: '#059669', fontWeight: 700 }}>{resolvedPct}%</Typography>
                      </Box>
                      <LinearProgress variant="determinate" value={resolvedPct}
                        sx={{ height: 4, borderRadius: 2, bgcolor: '#e2e8f0', '& .MuiLinearProgress-bar': { bgcolor: '#059669', borderRadius: 2 } }} />
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Tabs */}
      <Card sx={{ borderRadius: 3, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9', mb: 2.5, overflow: 'visible' }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto"
          sx={{
            px: 1, minHeight: 52,
            '& .MuiTab-root': { minHeight: 52, textTransform: 'none', fontWeight: 600, fontSize: '0.82rem', color: '#94a3b8', py: 1.5, '&.Mui-selected': { color: '#0f766e' } },
            '& .MuiTabs-indicator': { bgcolor: '#14b8a6', height: 3, borderRadius: '3px 3px 0 0' },
          }}>
          {TAB_DEFINITIONS.map((tab) => {
            const count = tab.supportType ? getTypeCount(tab.supportType) : (stats?.pending ?? 0);
            return (
              <Tab key={tab.key} label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  {React.cloneElement(tab.icon, { sx: { fontSize: 18, color: tab.color } })}
                  <span>{tab.label}</span>
                  {count > 0 && (
                    <Box sx={{ bgcolor: tab.color, color: '#fff', borderRadius: '10px', px: 0.8, py: 0.1, fontSize: '0.65rem', fontWeight: 700, minWidth: 18, textAlign: 'center', lineHeight: 1.6 }}>
                      {count}
                    </Box>
                  )}
                </Box>
              } />
            );
          })}
        </Tabs>
      </Card>

      {/* Filters */}
      <Card sx={{ borderRadius: 3, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9', mb: 2.5 }}>
        <CardContent sx={{ py: 2, px: 2.5, '&:last-child': { pb: 2 } }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} label="Status"
                  sx={{ borderRadius: 2, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' } }}>
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="pending">Pendente</MenuItem>
                  <MenuItem value="in_progress">Em Andamento</MenuItem>
                  <MenuItem value="resolved">Resolvido</MenuItem>
                  <MenuItem value="closed">Encerrado</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={8} md={9}>
              <TextField fullWidth size="small" placeholder="Buscar por nome, email ou telefone..."
                value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: '#94a3b8', fontSize: 20 }} /> }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, '& fieldset': { borderColor: '#e2e8f0' } } }} />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Leads List */}
      <Card sx={{ borderRadius: 3, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9', overflow: 'hidden' }}>
        {/* Section header */}
        <Box sx={{ px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 1.5, borderBottom: '1px solid #f1f5f9' }}>
          <Avatar sx={{ bgcolor: alpha(currentTabDef.color, 0.12), width: 36, height: 36, color: currentTabDef.color }}>
            {React.cloneElement(currentTabDef.icon, { sx: { fontSize: 18 } })}
          </Avatar>
          <Box>
            <Typography sx={{ fontWeight: 700, color: '#1e293b', fontSize: '0.95rem' }}>{currentTabDef.label}</Typography>
            <Typography sx={{ color: '#94a3b8', fontSize: '0.75rem' }}>{leads.length} solicitaç{leads.length !== 1 ? 'ões' : 'ão'}</Typography>
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress sx={{ color: '#14b8a6' }} /></Box>
        ) : leads.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Avatar sx={{ width: 56, height: 56, bgcolor: '#f0fdfa', mx: 'auto', mb: 2 }}>
              <SupportIcon sx={{ color: '#14b8a6' }} />
            </Avatar>
            <Typography sx={{ color: '#64748b', fontWeight: 500 }}>Nenhuma solicitação encontrada</Typography>
            <Typography sx={{ color: '#94a3b8', fontSize: '0.82rem', mt: 0.5 }}>Ajuste os filtros ou selecione outra categoria</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  {['Paciente', 'Status', 'Categoria', 'Data', 'Ações'].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 700, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em', py: 1.5, borderBottom: '1px solid #e2e8f0' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {leads.map((lead) => {
                  const typeInfo = getSupportTypeInfo(lead.support_type);
                  const convInfo = leadConversations.get(lead.contact_id);
                  const isExpanded = expandedLeadId === lead.id;
                  const st = STATUS_MAP[lead.status] || STATUS_MAP.pending;

                  return (
                    <React.Fragment key={lead.id}>
                      <TableRow
                        sx={{ '&:hover': { bgcolor: '#fafbfc' }, cursor: lead.comment ? 'pointer' : 'default', transition: 'background 0.15s' }}
                        onClick={() => { if (lead.comment) setExpandedLeadId(isExpanded ? null : lead.id); }}>

                        {/* Paciente */}
                        <TableCell sx={{ borderBottom: isExpanded ? 'none' : '1px solid #f1f5f9', py: 1.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            {lead.comment && (
                              <IconButton size="small" sx={{ p: 0, color: '#94a3b8' }}>
                                {isExpanded ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
                              </IconButton>
                            )}
                            <Avatar sx={{ bgcolor: alpha(typeInfo.color, 0.12), width: 36, height: 36, color: typeInfo.color, fontSize: '0.85rem', fontWeight: 700 }}>
                              {(lead.lead_name || 'P').charAt(0).toUpperCase()}
                            </Avatar>
                            <Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography sx={{ fontWeight: 600, color: '#1e293b', fontSize: '0.88rem' }}>{lead.lead_name}</Typography>
                                {lead.comment && <CommentIcon sx={{ fontSize: 14, color: '#94a3b8' }} />}
                              </Box>
                              {lead.lead_email && <Typography sx={{ color: '#94a3b8', fontSize: '0.75rem' }}>{lead.lead_email}</Typography>}
                              {lead.lead_phone && <Typography sx={{ color: '#94a3b8', fontSize: '0.75rem' }}>{lead.lead_phone}</Typography>}
                            </Box>
                          </Box>
                        </TableCell>

                        {/* Status */}
                        <TableCell sx={{ borderBottom: isExpanded ? 'none' : '1px solid #f1f5f9', py: 1.5 }}>
                          <Chip label={st.label} size="small" sx={{ fontWeight: 600, fontSize: '0.72rem', bgcolor: st.bg, color: st.color, border: `1px solid ${alpha(st.color, 0.2)}` }} />
                        </TableCell>

                        {/* Categoria */}
                        <TableCell sx={{ borderBottom: isExpanded ? 'none' : '1px solid #f1f5f9', py: 1.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            {React.cloneElement(typeInfo.icon, { sx: { fontSize: 16, color: typeInfo.color } })}
                            <Typography sx={{ fontSize: '0.82rem', fontWeight: 500, color: typeInfo.color }}>{typeInfo.label}</Typography>
                          </Box>
                        </TableCell>

                        {/* Data */}
                        <TableCell sx={{ borderBottom: isExpanded ? 'none' : '1px solid #f1f5f9', py: 1.5 }}>
                          <Typography sx={{ color: '#64748b', fontSize: '0.8rem' }}>{formatDate(lead.created_at)}</Typography>
                        </TableCell>

                        {/* Ações */}
                        <TableCell sx={{ borderBottom: isExpanded ? 'none' : '1px solid #f1f5f9', py: 1.5 }}>
                          <Stack direction="row" spacing={0.5} onClick={e => e.stopPropagation()}>
                            <Tooltip title="Ver detalhes" arrow>
                              <IconButton size="small" onClick={() => { setSelectedLead(lead); setOpenDialog(true); }}
                                sx={{ bgcolor: '#f0fdfa', color: '#0f766e', '&:hover': { bgcolor: '#ccfbf1' } }}>
                                <ViewIcon sx={{ fontSize: 17 }} />
                              </IconButton>
                            </Tooltip>
                            {resolvingChatLinks ? (
                              <Tooltip title="Carregando atalho do chat…" arrow>
                                <span>
                                  <IconButton size="small" disabled sx={{ bgcolor: '#f8fafc', width: 34, height: 34 }}>
                                    <CircularProgress size={16} thickness={5} sx={{ color: '#14b8a6' }} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            ) : (
                              <>
                            {convInfo?.hasWhatsApp && (
                              <Tooltip title="WhatsApp" arrow>
                                <IconButton size="small" onClick={() => navigate(`${convInfo.whatsappPath || '/inbox/whatsapp'}?contactId=${lead.contact_id}`)}
                                  sx={{ bgcolor: '#ecfdf5', color: '#059669', '&:hover': { bgcolor: '#d1fae5' } }}>
                                  <WhatsAppIcon sx={{ fontSize: 17 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            {convInfo?.hasTelegram && (
                              <Tooltip title="Telegram" arrow>
                                <IconButton size="small" onClick={() => navigate(`/inbox/telegram?contactId=${lead.contact_id}`)}
                                  sx={{ bgcolor: '#eff6ff', color: '#2563eb', '&:hover': { bgcolor: '#dbeafe' } }}>
                                  <TelegramIcon sx={{ fontSize: 17 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            {!convInfo?.hasWhatsApp && !convInfo?.hasTelegram && (
                              <Tooltip title="Sem conversa" arrow>
                                <IconButton size="small" disabled sx={{ opacity: 0.3 }}><ChatIcon sx={{ fontSize: 17 }} /></IconButton>
                              </Tooltip>
                            )}
                              </>
                            )}
                            <Tooltip title="Alterar status" arrow>
                              <IconButton size="small" onClick={() => { setSelectedLead(lead); setNewStatus(lead.status); setOpenStatusDialog(true); }}
                                sx={{ bgcolor: '#fffbeb', color: '#d97706', '&:hover': { bgcolor: '#fef3c7' } }}>
                                <StatusIcon sx={{ fontSize: 17 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Excluir" arrow>
                              <IconButton size="small" onClick={() => { setSelectedLead(lead); setOpenDeleteDialog(true); }}
                                sx={{ bgcolor: '#fef2f2', color: '#dc2626', '&:hover': { bgcolor: '#fee2e2' } }}>
                                <DeleteIcon sx={{ fontSize: 17 }} />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>

                      {/* Expandable comment */}
                      {lead.comment && (
                        <TableRow>
                          <TableCell colSpan={5} sx={{ py: 0, borderBottom: '1px solid #f1f5f9' }}>
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                              <Box sx={{ py: 1.5, px: 2, my: 1, bgcolor: '#f8fafb', borderRadius: 2, border: '1px solid #e8eef3' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                                  <CommentIcon sx={{ fontSize: 15, color: '#0f766e' }} />
                                  <Typography sx={{ fontWeight: 600, color: '#0f766e', fontSize: '0.78rem' }}>Observação do paciente</Typography>
                                </Box>
                                <Typography sx={{ color: '#334155', fontSize: '0.85rem', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{lead.comment}</Typography>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      {/* ──── Detail Dialog ──── */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
        {selectedLead && (() => {
          const ti = getSupportTypeInfo(selectedLead.support_type);
          const ci = leadConversations.get(selectedLead.contact_id);
          const stl = STATUS_MAP[selectedLead.status] || STATUS_MAP.pending;
          return (
            <>
              <Box sx={{ background: `linear-gradient(135deg, ${ti.color} 0%, ${alpha(ti.color, 0.8)} 100%)`, px: 3, py: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 48, height: 48, color: '#fff' }}>
                    {React.cloneElement(ti.icon, { sx: { fontSize: 24 } })}
                  </Avatar>
                  <Box>
                    <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.1rem' }}>{selectedLead.lead_name}</Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.82rem' }}>{ti.label}</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {resolvingChatLinks ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 40, height: 40 }}>
                      <CircularProgress size={22} thickness={4} sx={{ color: '#fff' }} />
                    </Box>
                  ) : (
                    <>
                  {ci?.hasWhatsApp && (
                    <IconButton size="small" onClick={() => navigate(`${ci.whatsappPath || '/inbox/whatsapp'}?contactId=${selectedLead.contact_id}`)}
                      sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}>
                      <WhatsAppIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  )}
                  {ci?.hasTelegram && (
                    <IconButton size="small" onClick={() => navigate(`/inbox/telegram?contactId=${selectedLead.contact_id}`)}
                      sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}>
                      <TelegramIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  )}
                    </>
                  )}
                  <IconButton size="small" onClick={() => setOpenDialog(false)} sx={{ color: '#fff' }}><CloseIcon /></IconButton>
                </Box>
              </Box>

              <DialogContent sx={{ px: 3, py: 2.5 }}>
                <Box sx={{ display: 'flex', gap: 1, mb: 2.5, flexWrap: 'wrap' }}>
                  <Chip label={stl.label} size="small" sx={{ fontWeight: 600, bgcolor: stl.bg, color: stl.color, border: `1px solid ${alpha(stl.color, 0.2)}` }} />
                </Box>

                <Grid container spacing={2}>
                  {selectedLead.lead_email && (
                    <Grid item xs={12} sm={6}>
                      <Typography sx={{ color: '#94a3b8', fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>E-mail</Typography>
                      <Typography sx={{ color: '#1e293b', fontWeight: 500, fontSize: '0.9rem' }}>{selectedLead.lead_email}</Typography>
                    </Grid>
                  )}
                  {selectedLead.lead_phone && (
                    <Grid item xs={12} sm={6}>
                      <Typography sx={{ color: '#94a3b8', fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Telefone</Typography>
                      <Typography sx={{ color: '#1e293b', fontWeight: 500, fontSize: '0.9rem' }}>{selectedLead.lead_phone}</Typography>
                    </Grid>
                  )}
                  <Grid item xs={12} sm={6}>
                    <Typography sx={{ color: '#94a3b8', fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Criado em</Typography>
                    <Typography sx={{ color: '#1e293b', fontWeight: 500, fontSize: '0.9rem' }}>{formatDate(selectedLead.created_at)}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography sx={{ color: '#94a3b8', fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Atualizado em</Typography>
                    <Typography sx={{ color: '#1e293b', fontWeight: 500, fontSize: '0.9rem' }}>{formatDate(selectedLead.updated_at)}</Typography>
                  </Grid>
                </Grid>

                {selectedLead.comment && (
                  <Box sx={{ mt: 2.5, p: 2, bgcolor: '#f8fafb', borderRadius: 2, border: '1px solid #e8eef3' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                      <CommentIcon sx={{ fontSize: 16, color: '#0f766e' }} />
                      <Typography sx={{ fontWeight: 600, color: '#0f766e', fontSize: '0.82rem' }}>Observação do Paciente</Typography>
                    </Box>
                    <Typography sx={{ color: '#334155', fontSize: '0.88rem', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{selectedLead.comment}</Typography>
                  </Box>
                )}

                {selectedLead.notes && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: '#fffbeb', borderRadius: 2, border: '1px solid #fde68a' }}>
                    <Typography sx={{ fontWeight: 600, color: '#92400e', fontSize: '0.82rem', mb: 0.5 }}>Notas</Typography>
                    <Typography sx={{ color: '#78350f', fontSize: '0.88rem', whiteSpace: 'pre-wrap' }}>{selectedLead.notes}</Typography>
                  </Box>
                )}

                {selectedLead.status === 'pending' && (
                  <Box sx={{ mt: 3 }}>
                    <Divider sx={{ mb: 2 }} />
                    <Typography sx={{ fontWeight: 700, color: '#1e293b', fontSize: '0.95rem', mb: 1.5 }}>Resolver Solicitação</Typography>
                    <TextField fullWidth multiline rows={3} label="Notas de resolução" placeholder="Descreva como a solicitação foi resolvida..."
                      value={resolveNotes} onChange={(e) => setResolveNotes(e.target.value)}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                  </Box>
                )}
              </DialogContent>

              <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                <Button onClick={() => setOpenDialog(false)} sx={{ color: '#64748b', textTransform: 'none', fontWeight: 600 }}>Fechar</Button>
                {selectedLead.status === 'pending' && (
                  <Button variant="contained" onClick={() => handleResolveLead(selectedLead.id)} startIcon={<ResolvedIcon />}
                    sx={{ bgcolor: '#059669', '&:hover': { bgcolor: '#047857' }, textTransform: 'none', fontWeight: 600, borderRadius: 2, px: 3 }}>
                    Resolver Solicitação
                  </Button>
                )}
              </DialogActions>
            </>
          );
        })()}
      </Dialog>

      {/* ──── Status Dialog ──── */}
      <Dialog open={openStatusDialog} onClose={() => setOpenStatusDialog(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
        <Box sx={{ background: 'linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)', px: 3, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <StatusIcon sx={{ color: '#fff' }} />
            <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1rem' }}>Alterar Status</Typography>
          </Box>
          <IconButton size="small" onClick={() => setOpenStatusDialog(false)} sx={{ color: '#fff' }}><CloseIcon /></IconButton>
        </Box>
        <DialogContent sx={{ pt: 3 }}>
          {selectedLead && (
            <Typography sx={{ color: '#64748b', fontSize: '0.85rem', mb: 2 }}>
              Paciente: <strong style={{ color: '#1e293b' }}>{selectedLead.lead_name}</strong>
            </Typography>
          )}
          <FormControl fullWidth size="small">
            <InputLabel>Novo Status</InputLabel>
            <Select value={newStatus} onChange={(e) => setNewStatus(e.target.value)} label="Novo Status" sx={{ borderRadius: 2 }}>
              {Object.entries(STATUS_MAP).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setOpenStatusDialog(false)} sx={{ color: '#64748b', textTransform: 'none' }}>Cancelar</Button>
          <Button variant="contained" onClick={() => selectedLead && handleUpdateStatus(selectedLead.id)}
            sx={{ bgcolor: '#0f766e', '&:hover': { bgcolor: '#0d6560' }, textTransform: 'none', fontWeight: 600, borderRadius: 2 }}>
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      {/* ──── Delete Dialog ──── */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
        <Box sx={{ background: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)', px: 3, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DeleteIcon sx={{ color: '#fff' }} />
            <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1rem' }}>Excluir Solicitação</Typography>
          </Box>
          <IconButton size="small" onClick={() => setOpenDeleteDialog(false)} sx={{ color: '#fff' }}><CloseIcon /></IconButton>
        </Box>
        <DialogContent sx={{ pt: 3 }}>
          <Typography sx={{ color: '#1e293b', mb: 2 }}>
            Deseja excluir a solicitação de <strong>{selectedLead?.lead_name}</strong>?
          </Typography>
          <Alert severity="warning" sx={{ borderRadius: 2 }}>Esta ação não pode ser desfeita.</Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setOpenDeleteDialog(false)} sx={{ color: '#64748b', textTransform: 'none' }}>Cancelar</Button>
          <Button variant="contained" onClick={() => selectedLead && handleDeleteLead(selectedLead.id)}
            sx={{ bgcolor: '#dc2626', '&:hover': { bgcolor: '#b91c1c' }, textTransform: 'none', fontWeight: 600, borderRadius: 2 }}>
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ borderRadius: 2 }}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default SupportDashboard;
